import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Row, Col, Typography, Card } from "antd";
import { useTheme } from "../../../contexts/ThemeContext";
import { Toast } from "../../ui";
import TotalizadorCard from "../../card/IndicatorCard/TotalizadorCard";
import SynchronizationService from "../../../services/synchronizationService";
import AiAutoConfigButton from "../../../features/dre-ai-config/components/AiAutoConfigButton";
import { AI_CONFIG_TARGET } from "../../../features/dre-ai-config/api/dreAiConfigApi";
import { mergeDreSelections } from "../../../features/dre-ai-config/utils/mergeSuggestion";

const { Text, Title } = Typography;

// IndicatorType (backend) -> chave do estado `totalizadores`, na ordem dos cards
const INDICATOR_KEY_BY_TYPE = {
  4: "receitaBruta",
  3: "receitaLiquida",
  6: "impostos",
  2: "ebitda",
  1: "lucroLiquido",
  5: "lucroBruto",
};
const INDICATOR_LABELS = {
  4: "Receita Bruta",
  3: "Receita Líquida",
  6: "Impostos",
  2: "EBITDA",
  1: "Lucro Líquido",
  5: "Lucro Bruto",
};

const FinancialIndicatorsConfig = ({
  categoryList,
  syncId,
  existingConfig = {},
  dreNodes = [],
  onKpiChange,
}) => {
  const { theme } = useTheme();

  const [total, setTotal] = useState([]);
  const [totalizadoresOptions, setTotalizadoresOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  // Itens existentes para suportar upsert (Id pode vir depois)
  const [existingKpiDre, setExistingKpiDre] = useState(
    existingConfig?.kpiDre || [],
  );
  // Estados para os totalizadores base (multi GUIDs)
  const [totalizadores, setTotalizadores] = useState({
    receitaBruta: { selected: [], liquidValue: null },
    receitaLiquida: { selected: [], liquidValue: null },
    ebitda: { selected: [], liquidValue: null },
    lucroLiquido: { selected: [], liquidValue: null },
    lucroBruto: { selected: [], liquidValue: null },
    impostos: { selected: [], liquidValue: null },
  });

  const indicatorTypes = useMemo(
    () => ({
      LucroLiquido: 1,
      EBITDA: 2,
      ReceitaLiquida: 3,
      ReceitaBruta: 4,
      LucroBruto: 5,
      Impostos: 6,
    }),
    [],
  );

  // Estados para as fórmulas (reservado para uso futuro na UI) — removido

  // -------------------------------
  // Load inicial (usar dados existentes OU buscar do backend)
  // -------------------------------
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        // 1) Carregar KPIs existentes para upsert
        let kpiDtos = [];
        if (
          existingConfig &&
          (Array.isArray(existingConfig.kpiDre) ||
            Array.isArray(existingConfig.syncKPIDREDtos))
        ) {
          kpiDtos =
            existingConfig.kpiDre || existingConfig.syncKPIDREDtos || [];
        } else if (syncId) {
          const syncKpidre =
            await SynchronizationService.getSynchronizationKPIDRE(syncId);
          kpiDtos = syncKpidre?.syncKPIDREDtos || [];
        }
        if (cancelled) return;
        setExistingKpiDre(kpiDtos);

        // Prepopular seleções conforme tipos existentes
        const byType = (t) => kpiDtos.find((x) => (x.type ?? x.Type) === t);
        setTotalizadores((prev) => ({
          ...prev,
          receitaLiquida: {
            ...prev.receitaLiquida,
            selected: byType(indicatorTypes.ReceitaLiquida)?.dre || [],
          },
          receitaBruta: {
            ...prev.receitaBruta,
            selected: byType(indicatorTypes.ReceitaBruta)?.dre || [],
          },
          ebitda: {
            ...prev.ebitda,
            selected: byType(indicatorTypes.EBITDA)?.dre || [],
          },
          lucroLiquido: {
            ...prev.lucroLiquido,
            selected: byType(indicatorTypes.LucroLiquido)?.dre || [],
          },
          lucroBruto: {
            ...prev.lucroBruto,
            selected: byType(indicatorTypes.LucroBruto)?.dre || [],
          },
          impostos: {
            ...prev.impostos,
            selected: byType(indicatorTypes.Impostos)?.dre || [],
          },
        }));

        // 2) DRE nodes para montar opções (prioriza dreNodes do pai)
        if (Array.isArray(dreNodes) && dreNodes.length > 0) {
          setTotal(dreNodes);
        } else if (syncId) {
          const dreConf =
            await SynchronizationService.getSynchronizationDreConfigTotalizadores(
              syncId,
            );
          if (cancelled) return;
          setTotal(dreConf?.syncDre || []);
        } else {
          setTotal([]);
        }
      } catch (err) {
        console.error("Erro ao carregar configurações de indicadores:", err);
        Toast.error("Erro ao carregar configurações");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [syncId, existingConfig, indicatorTypes, dreNodes]);

  // Atualiza DRE tree em tempo real quando vier do pai
  useEffect(() => {
    if (Array.isArray(dreNodes) && dreNodes.length > 0) {
      setTotal(dreNodes);
    }
  }, [dreNodes]);

  // -----------------------------------------
  // Monta TreeSelect (Totalizador → Subconta → Conta)
  // -----------------------------------------
  useEffect(() => {
    const buildTreeFromDre = (items) => {
      // Filtra nós deletados E inativos
      const valid = (items || []).filter(
        (i) => !i.isDeleted && i.isActive !== false,
      );

      const codeToNode = {};

      // Cria nós básicos (value/key = id, code = codDRE)
      valid.forEach((i) => {
        const title = i.descricao || i.descricaoOriginal || i.codDRE;
        const node = {
          title,
          value: i.id,
          key: i.id,
          code: i.codDRE,
          position: i.position ?? i.Position,
          parentCode: i.codDRESup || i.codDreSup,
          children: [],
        };
        codeToNode[i.codDRE] = node;
      });

      // Liga hierarquia via codDRESup => codDRE
      const roots = [];
      Object.values(codeToNode).forEach((node) => {
        if (node.parentCode && codeToNode[node.parentCode]) {
          codeToNode[node.parentCode].children.push(node);
        } else {
          roots.push(node);
        }
      });

      const sortByPosition = (a, b) => {
        const aPos = Number.isFinite(Number(a.position))
          ? Number(a.position)
          : Number.MAX_SAFE_INTEGER;
        const bPos = Number.isFinite(Number(b.position))
          ? Number(b.position)
          : Number.MAX_SAFE_INTEGER;
        if (aPos !== bPos) return aPos - bPos;
        return (a.code || "").localeCompare(b.code || "");
      };
      const sortTree = (nodes) => {
        nodes.sort(sortByPosition);
        nodes.forEach(
          (n) => n.children && n.children.length && sortTree(n.children),
        );
      };
      sortTree(roots);

      return { tree: roots };
    };

    const { tree } = buildTreeFromDre(total);
    setTotalizadoresOptions(tree);
  }, [total]);

  // -----------------------------------------
  // Índices para sanitização e hierarquia
  // -----------------------------------------
  const { idIndex, allowedIds } = useMemo(() => {
    // Cria um índice por id e também consegue recuperar parentId via code->id
    const idx = {};
    const codeToId = {};

    const walk = (nodes, parentId = null) => {
      (nodes || []).forEach((n) => {
        codeToId[n.code] = n.value;
        idx[n.value] = {
          id: n.value,
          title: n.title,
          code: n.code,
          parentCode: n.parentCode,
          parentId, // preenchido após segunda passada
          children: n.children || [],
        };
        if (n.children && n.children.length) {
          walk(n.children, n.value);
        }
      });
    };
    walk(totalizadoresOptions);

    // Preenche parentId usando o mapa de códigos
    Object.values(idx).forEach((entry) => {
      if (!entry.parentId && entry.parentCode && codeToId[entry.parentCode]) {
        entry.parentId = codeToId[entry.parentCode];
      }
    });

    return {
      idIndex: idx,
      allowedIds: new Set(Object.keys(idx)),
    };
  }, [totalizadoresOptions]);

  const getAncestors = useCallback(
    (id) => {
      const res = [];
      let cur = idIndex[id];
      while (cur && cur.parentId) {
        res.push(cur.parentId);
        cur = idIndex[cur.parentId];
      }
      return res;
    },
    [idIndex],
  );

  const dropUnknownIds = useCallback(
    (ids) => (Array.isArray(ids) ? ids.filter((id) => allowedIds.has(id)) : []),
    [allowedIds],
  );

  const canonicalizeByHierarchy = useCallback(
    (ids) => {
      const filtered = dropUnknownIds(ids);
      const set = new Set(filtered);
      for (const id of filtered) {
        const ancestors = getAncestors(id);
        // Se algum ancestral está selecionado, remove o id (fica só o maior)
        if (ancestors.some((a) => set.has(a))) {
          set.delete(id);
        }
      }
      return Array.from(set);
    },
    [dropUnknownIds, getAncestors],
  );

  const sanitizeSelection = useCallback(
    (value) => {
      const arr = Array.isArray(value) ? value : [];
      return canonicalizeByHierarchy(arr);
    },
    [canonicalizeByHierarchy],
  );

  // Sanitiza as seleções carregadas do backend assim que os índices estão prontos
  useEffect(() => {
    if (!Object.keys(idIndex).length) return;
    setTotalizadores((prev) => ({
      receitaBruta: {
        ...prev.receitaBruta,
        selected: sanitizeSelection(prev.receitaBruta.selected),
      },
      receitaLiquida: {
        ...prev.receitaLiquida,
        selected: sanitizeSelection(prev.receitaLiquida.selected),
      },
      ebitda: {
        ...prev.ebitda,
        selected: sanitizeSelection(prev.ebitda.selected),
      },
      lucroLiquido: {
        ...prev.lucroLiquido,
        selected: sanitizeSelection(prev.lucroLiquido.selected),
      },
      lucroBruto: {
        ...prev.lucroBruto,
        selected: sanitizeSelection(prev.lucroBruto.selected),
      },
      impostos: {
        ...prev.impostos,
        selected: sanitizeSelection(prev.impostos.selected),
      },
    }));
  }, [idIndex, sanitizeSelection]);

  // Remove IDs de nodes desabilitados/deletados das seleções
  useEffect(() => {
    if (!total.length) return;

    // IDs de nodes ativos (não deletados e ativos)
    const activeIds = new Set(
      total
        .filter((node) => !node.isDeleted && node.isActive !== false)
        .map((node) => node.id),
    );

    // Limpa seleções que contenham IDs inativos
    setTotalizadores((prev) => {
      const updated = {};
      let hasChanges = false;

      Object.keys(prev).forEach((key) => {
        const currentSelected = prev[key].selected || [];
        const filteredSelected = currentSelected.filter((id) =>
          activeIds.has(id),
        );

        if (filteredSelected.length !== currentSelected.length) {
          hasChanges = true;
        }

        updated[key] = {
          ...prev[key],
          selected: filteredSelected,
        };
      });

      return hasChanges ? updated : prev;
    });
  }, [total]);

  // Helpers de rótulo removidos

  // Mapa de Type -> Id existente para upsert
  const existingIdByType = useMemo(() => {
    const map = {};
    (existingKpiDre || []).forEach((item) => {
      const type =
        item?.Type ?? item?.type ?? item?.IndicatorType ?? item?.indicatorType;
      const id = item?.Id ?? item?.id;
      if (type != null && id) {
        map[type] = id;
      }
    });
    return map;
  }, [existingKpiDre]);

  // ---------------------------
  // Handlers
  // ---------------------------
  const handleTotalizadorChange = (field, subField, value) => {
    setTotalizadores((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        [subField]: value,
      },
    }));
  };

  // const handleFormulaChange = (field, subField, value) => {
  //   setFormulas(prev => ({
  //     ...prev,
  //     [field]: {
  //       ...prev[field],
  //       [subField]: value
  //     }
  //   }));
  // };

  // Emite o payload sempre que as seleções mudarem para o pai salvar
  const buildKpiPayload = useCallback(() => {
    const buildItem = (type, dreArray) => {
      const sanitized = sanitizeSelection(dreArray);
      const base = { SyncId: syncId, Type: type, Dre: sanitized };
      const maybeId = existingIdByType[type];
      return maybeId ? { ...base, Id: maybeId } : base;
    };
    return [
      buildItem(
        1 * indicatorTypes.ReceitaBruta,
        totalizadores.receitaBruta.selected,
      ),
      buildItem(
        1 * indicatorTypes.ReceitaLiquida,
        totalizadores.receitaLiquida.selected,
      ),
      buildItem(1 * indicatorTypes.EBITDA, totalizadores.ebitda.selected),
      buildItem(
        1 * indicatorTypes.LucroLiquido,
        totalizadores.lucroLiquido.selected,
      ),
      buildItem(
        1 * indicatorTypes.LucroBruto,
        totalizadores.lucroBruto.selected,
      ),
      buildItem(1 * indicatorTypes.Impostos, totalizadores.impostos.selected),
    ].filter((item) => Array.isArray(item.Dre) && item.Dre.length > 0);
  }, [
    existingIdByType,
    sanitizeSelection,
    syncId,
    totalizadores,
    indicatorTypes,
  ]);

  useEffect(() => {
    if (typeof onKpiChange === "function") {
      onKpiChange(buildKpiPayload());
    }
  }, [buildKpiPayload, onKpiChange]);

  // Aplica a sugestão da IA no estado da tela (persistência continua no "Salvar Alterações")
  const handleApplyAiSuggestion = (suggestion, mode) => {
    const currentByType = {};
    Object.entries(INDICATOR_KEY_BY_TYPE).forEach(([type, key]) => {
      currentByType[type] = totalizadores[key]?.selected || [];
    });
    const merged = mergeDreSelections(currentByType, suggestion?.indicators, mode);
    setTotalizadores((prev) => {
      const next = { ...prev };
      Object.entries(INDICATOR_KEY_BY_TYPE).forEach(([type, key]) => {
        next[key] = { ...prev[key], selected: sanitizeSelection(merged[type] || []) };
      });
      return next;
    });
  };

  const hasExistingSelection = Object.values(totalizadores).some(
    (t) => (t.selected || []).length > 0,
  );

  // ---------------------------
  // Render
  // ---------------------------
  if (loading) {
    return null;
  }

  return (
    <div style={{ minHeight: "100vh" }}>
      <div style={{ padding: 0 }}>
        <Row gutter={24}>
          {/* Coluna Esquerda - Configurações */}
          <Col span={24}>
            {/* Totalizadores Base */}
            <Card
              title={
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      backgroundColor: theme.infoBackground,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Text style={{ fontWeight: "bold", color: theme.primary }}>
                      B
                    </Text>
                  </div>
                  <div>
                    <Text strong style={{ color: theme.textPrimary }}>
                      Totalizadores Base
                    </Text>
                    <Text
                      style={{
                        display: "block",
                        fontSize: 12,
                        color: theme.textSecondary,
                      }}
                    >
                      Selecione as contas / totalizadores para cada indicador
                    </Text>
                  </div>
                  {/* Fórmulas de Indicadores - removidas da UI por ora */}
                  <div style={{ marginLeft: "auto" }}>
                    <AiAutoConfigButton
                      syncId={syncId}
                      target={AI_CONFIG_TARGET.dre}
                      typeLabels={INDICATOR_LABELS}
                      hasExisting={hasExistingSelection}
                      disabled={totalizadoresOptions.length === 0}
                      disabledReason="Salve a estrutura do DRE antes de configurar os indicadores."
                      onApply={handleApplyAiSuggestion}
                    />
                  </div>
                </div>
              }
              style={{
                marginBottom: 24,
                height: "100%",
                backgroundColor: theme.cardBackground,
                border: `1px solid ${theme.border}`,
              }}
              styles={{
                body: { height: "calc(100% - 57px)", overflowY: "auto" },
              }}
            >
              <TotalizadorCard
                title="Receita Bruta"
                subtitle="Selecione um totalizador..."
                selected={totalizadores.receitaBruta.selected}
                liquidValue={totalizadores.receitaBruta.liquidValue}
                onChangeSelected={(value) =>
                  handleTotalizadorChange(
                    "receitaBruta",
                    "selected",
                    sanitizeSelection(value),
                  )
                }
                onChangeLiquidValue={(value) =>
                  handleTotalizadorChange("receitaBruta", "liquidValue", value)
                }
                options={totalizadoresOptions}
              />
              <TotalizadorCard
                title="Receita Líquida"
                subtitle="Selecione um totalizador..."
                selected={totalizadores.receitaLiquida.selected}
                liquidValue={totalizadores.receitaLiquida.liquidValue}
                onChangeSelected={(value) =>
                  handleTotalizadorChange(
                    "receitaLiquida",
                    "selected",
                    sanitizeSelection(value),
                  )
                }
                onChangeLiquidValue={(value) =>
                  handleTotalizadorChange(
                    "receitaLiquida",
                    "liquidValue",
                    value,
                  )
                }
                options={totalizadoresOptions}
              />
              <TotalizadorCard
                title="Impostos"
                subtitle="Selecione um totalizador..."
                selected={totalizadores.impostos.selected}
                liquidValue={totalizadores.impostos.liquidValue}
                onChangeSelected={(value) =>
                  handleTotalizadorChange(
                    "impostos",
                    "selected",
                    sanitizeSelection(value),
                  )
                }
                onChangeLiquidValue={(value) =>
                  handleTotalizadorChange("impostos", "liquidValue", value)
                }
                options={totalizadoresOptions}
              />
              <TotalizadorCard
                title="Lucro Bruto"
                subtitle="Selecione um totalizador..."
                selected={totalizadores.lucroBruto.selected}
                liquidValue={totalizadores.lucroBruto.liquidValue}
                onChangeSelected={(value) =>
                  handleTotalizadorChange(
                    "lucroBruto",
                    "selected",
                    sanitizeSelection(value),
                  )
                }
                onChangeLiquidValue={(value) =>
                  handleTotalizadorChange("lucroBruto", "liquidValue", value)
                }
                options={totalizadoresOptions}
              />
              <TotalizadorCard
                title="EBITDA"
                subtitle="Selecione um totalizador..."
                selected={totalizadores.ebitda.selected}
                liquidValue={totalizadores.ebitda.liquidValue}
                onChangeSelected={(value) =>
                  handleTotalizadorChange(
                    "ebitda",
                    "selected",
                    sanitizeSelection(value),
                  )
                }
                onChangeLiquidValue={(value) =>
                  handleTotalizadorChange("ebitda", "liquidValue", value)
                }
                options={totalizadoresOptions}
              />
              <TotalizadorCard
                title="Lucro Líquido"
                subtitle="Selecione um totalizador..."
                selected={totalizadores.lucroLiquido.selected}
                liquidValue={totalizadores.lucroLiquido.liquidValue}
                onChangeSelected={(value) =>
                  handleTotalizadorChange(
                    "lucroLiquido",
                    "selected",
                    sanitizeSelection(value),
                  )
                }
                onChangeLiquidValue={(value) =>
                  handleTotalizadorChange("lucroLiquido", "liquidValue", value)
                }
                options={totalizadoresOptions}
              />
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default FinancialIndicatorsConfig;
